import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const API_BASE = `${import.meta.env.VITE_API_BASE ?? ''}/api/v1`;
const TOKEN_KEY = 'lineageatlas_token';

interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Request a magic sign-in link. Resolves with an optional dev link (local only). */
  requestMagicLink: (email: string) => Promise<{ devLink?: string }>;
  /** Exchange a magic-link token for a session. */
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [isReady, setIsReady] = useState(false);

  // On mount, validate existing token
  useEffect(() => {
    async function validate() {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (!stored) {
        setIsReady(true);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${stored}` },
        });
        if (res.ok) {
          const json = (await res.json()) as { data: User };
          setUser(json.data);
          setToken(stored);
        } else {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        }
      } catch {
        // Network error — keep token, let user retry
      } finally {
        setIsReady(true);
      }
    }
    void validate();
  }, []);

  const setSession = useCallback((newToken: string, newUser: User) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Login failed (${res.status})`);
      }
      const json = (await res.json()) as { data: { token: string; user: User } };
      setSession(json.data.token, json.data.user);
    },
    [setSession],
  );

  const requestMagicLink = useCallback(async (email: string) => {
    const res = await fetch(`${API_BASE}/auth/magic-link/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `Could not send sign-in link (${res.status})`);
    }
    const json = (await res.json()) as { devLink?: string };
    return { devLink: json.devLink };
  }, []);

  const verifyMagicLink = useCallback(
    async (linkToken: string) => {
      const res = await fetch(`${API_BASE}/auth/magic-link/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: linkToken }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'This sign-in link is invalid or has expired.');
      }
      const json = (await res.json()) as { data: { token: string; user: User } };
      setSession(json.data.token, json.data.user);
    },
    [setSession],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  if (!isReady) return null;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        login,
        requestMagicLink,
        verifyMagicLink,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
